import { useEffect } from 'react';
import { useLocation, Navigate, useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useCrearInscripcionMutation, useInscripcionProgress, useUpdateBeneficiarioMutation, useCotizacionQuery, useOpcionesAltaQuery } from '../services/hooks';
import { useRole } from '../hooks/useRole';
import { SelectorTarifas, esErrorDePrecio } from '../components/suscripciones/PrecioSuscripcion';
import { useUser } from '@clerk/clerk-react';
import { useState, useMemo } from 'react';
import { ClienteForm } from '../components/forms/ClienteForm';
import { SuscripcionForm } from '../components/forms/SuscripcionForm';
import { PlazasResumen } from '../components/inscripcion/PlazasResumen';
import { FormButtons } from '../components/common/FormButtons';
import { schema } from '../validations/inscripcionSchema';
import { FormValues } from '../types/forms';
import { toast } from 'react-toastify';
import { TelefonoValidacion } from '../components/inscripcion/TelefonoValidacion';
import { Cliente } from '../types/clientes';
import { validateIban, cleanIban } from '../utils/ibanValidation';
import { useChoquesPlazasQuery, esChoquesCambiados } from '../services/hooks/usePlazas';
import { ChoquesPlazasAviso, Decisiones, decisionesPorDefecto, idsChoques } from '../components/plazas/ChoquesPlazasAviso';
import { ResumenClasesCompletas } from '../components/plazas/ResumenClasesCompletas';

interface PlazaDisponible {
    dia: string;
    horaInicio: string;
    nivel_id: string;
    nivel_nombre: string;
}

interface LocationState {
    plazasDisponibles: PlazaDisponible[];
    frecuenciaSemanal: number;
    // D2 — persona de prueba que se está convirtiendo en cliente.
    prospect?: { id: string; name: string; phone: string; email: string };
}

const UNLIMITED_WEEKLY_SESSIONS = -1;

const frequencyFromState = (frecuenciaSemanal?: number, plazasCount = 0) => {
    if (frecuenciaSemanal === UNLIMITED_WEEKLY_SESSIONS) {
        return 'Ilimitada';
    }

    return `${plazasCount}XSemana`;
};



const InscripcionCliente = () => {
    const { user } = useUser();
    const [tipoPagoSeleccionado, setTipoPagoSeleccionado] = useState<string>('');
    const [suscripcionSeleccionada, setSuscripcionSeleccionada] = useState<string>('');
    const [isPagadorDiferente, setIsPagadorDiferente] = useState<boolean>(false);
    const [ibanFormErrors, setIbanFormErrors] = useState({
        domiciliacion: '',
        pagadorExterno: ''
    });
    const navigate = useNavigate();
    const location = useLocation();
    const { tenantCode } = useParams<{ tenantCode: string }>();
    const state = location.state as LocationState;
    const crearInscripcion = useCrearInscripcionMutation();
    const [selectedPagadorId, setSelectedPagadorId] = useState<string | undefined>(undefined);

    const [sessionId, setSessionId] = useState<string | undefined>();
    const { data: progress } = useInscripcionProgress(sessionId);
    const [isProcessing, setIsProcessing] = useState(false);

    const updateBeneficiario = useUpdateBeneficiarioMutation();

    // Casilla "Invitar al portal": arranca como diga el ajuste del centro y se
    // puede cambiar en cada alta. null = el operador aún no la ha tocado.
    const opcionesAlta = useOpcionesAltaQuery();
    const [invitarPortalElegido, setInvitarPortalElegido] = useState<boolean | null>(null);
    const invitarPortal = invitarPortalElegido ?? opcionesAlta.data?.invitarPortal ?? true;

    const { 
        register, 
        handleSubmit, 
        formState: { errors, isSubmitting },
        watch,
        setValue,
    } = useForm<FormValues>({
        resolver: yupResolver(schema) as any,
        mode: 'onBlur'
    });

    // D2 — datos que ya dio cuando vino a probar. El DNI no: es justo lo que
    // no se le pidió entonces y lo que hace falta ahora para ser cliente.
    useEffect(() => {
        if (!state?.prospect) return;
        setValue('nombre', state.prospect.name);
        setValue('email', state.prospect.email);
        setValue('telefono', state.prospect.phone);
    }, [state?.prospect, setValue]);

    // Clases ya generadas de estas plazas que están llenas (recuperaciones,
    // clases de prueba…): el cliente no entrará en ellas. Se avisan antes de
    // confirmar y el operador decide si compensa cada una con una recuperación.
    const plazasBusqueda = useMemo(
        () => (state?.plazasDisponibles ?? []).map(({ dia, horaInicio, nivel_id }) => ({ dia, horaInicio, nivel_id })),
        [state?.plazasDisponibles]
    );
    // Se congela al guardar: la vista previa de un cliente NUEVO no sabe quién
    // es, así que en cuanto queda apuntado vería sus propias clases como llenas
    // "por otra persona". Si el guardado no llega a hacerse (409), se reactiva.
    const choques = useChoquesPlazasQuery(plazasBusqueda, {
        prospectId: state?.prospect?.id,
        enabled: !isProcessing && !sessionId,
    });
    const [decisiones, setDecisiones] = useState<Decisiones>({});
    useEffect(() => {
        // Un refetch no debe pisar lo que el operador ya haya marcado.
        if (choques.data) setDecisiones((prev) => ({ ...decisionesPorDefecto(choques.data), ...prev }));
    }, [choques.data]);
    const nombreNivel = (nivelId: string) =>
        state?.plazasDisponibles?.find((p) => p.nivel_id === nivelId)?.nivel_nombre;

    // Precio: lo calcula el back con las tarifas por nivel (ver pricingService).
    // Si un nivel tiene varias tarifas (máquina fundador / general) se elige aquí.
    const { isAdmin, isSuperAdmin } = useRole();
    const puedeFijarPrecio = isAdmin || isSuperAdmin;
    const [tarifasElegidas, setTarifasElegidas] = useState<Record<string, string>>({});
    const [precioFijo, setPrecioFijo] = useState(false);
    const esIlimitada = state?.frecuenciaSemanal === UNLIMITED_WEEKLY_SESSIONS;
    const tarifasAsignadas = useMemo(() => Object.values(tarifasElegidas).filter(Boolean), [tarifasElegidas]);
    const cotizacion = useCotizacionQuery({
        plazas: plazasBusqueda,
        tarifasAsignadas,
        enabled: !esIlimitada && !precioFijo,
    });

    // Estados para validación de teléfono
    const [clienteEncontrado, setClienteEncontrado] = useState<Cliente | null>(null);
    const [telefonoValido, setTelefonoValido] = useState<boolean>(true);
    const [telefonoParaValidar, setTelefonoParaValidar] = useState<string>(''); // ← Nuevo estado

    // Función para manejar onBlur del teléfono
    const handleTelefonoBlur = (telefono: string) => {
        if (telefono && telefono.trim() !== '' && telefono.length >= 9) {
            setTelefonoParaValidar(telefono.trim());
        } else {
            setTelefonoParaValidar('');
            setClienteEncontrado(null);
            setTelefonoValido(true);
        }
    };

    // Añadir esto para debug
    // console.log('Form Values:', watch());
    // console.log('Form Errors:', errors);
    // Comentado: se imprimía en cada repintado de la pantalla.
    // console.log('plazasDisponibles en estado:', state?.plazasDisponibles);

    if (!state?.plazasDisponibles) {
        return <Navigate to="/" replace />;
    }

    const handleTipoPago = (tipoPago: string) => {

        setTipoPagoSeleccionado(tipoPago);
        if(tipoPago === 'Domiciliaciones' || tipoPago === 'Pagador - Cliente' || tipoPago === 'Pagador - No Cliente'){
            setSuscripcionSeleccionada('pilates-domiciliacion');
            setIsPagadorDiferente(tipoPago === 'Pagador - No Cliente');
        } else {
            setSuscripcionSeleccionada('pilates-otros-pagos');
        }
    };

    const handlePagadorSelect = (pagadorId: string) => {
        setSelectedPagadorId(pagadorId);
    };

    // Calcular si el cliente es menor de edad (< 18 años)
    const fechaNacimiento = watch('fechaNacimiento');
    const esMenorDeEdad = useMemo(() => {
        if (!fechaNacimiento) return false;
        
        const hoy = new Date();
        const fechaNac = new Date(fechaNacimiento);
        const edad = hoy.getFullYear() - fechaNac.getFullYear();
        const mesActual = hoy.getMonth();
        const mesNacimiento = fechaNac.getMonth();
        
        // Ajustar si aún no ha cumplido años este año
        if (mesActual < mesNacimiento || (mesActual === mesNacimiento && hoy.getDate() < fechaNac.getDate())) {
            return (edad - 1) < 18;
        }
        
        return edad < 18;
    }, [fechaNacimiento]);

    const onSubmit = async (data: FormValues) => {
        // Validar que no hay cliente duplicado antes de enviar
        if (clienteEncontrado) {
            toast.error('No se puede crear el cliente. Ya existe un cliente con este teléfono.');
            return;
        }

        // Validar IBANs antes de enviar si están presentes
        let hasIbanErrors = false;
        
        // Validar IBAN de domiciliación si existe
        if (data.iban && data.iban.trim()) {
            const ibanValidation = validateIban(data.iban);
            if (!ibanValidation.isValid) {
                setIbanFormErrors(prev => ({ 
                    ...prev, 
                    domiciliacion: ibanValidation.error || 'Hay un error en el formato del IBAN' 
                }));
                hasIbanErrors = true;
            }
        }
        
        // Validar IBAN del pagador si existe
        if (data.pagador?.IBAN && data.pagador.IBAN.trim()) {
            const ibanValidation = validateIban(data.pagador.IBAN);
            if (!ibanValidation.isValid) {
                setIbanFormErrors(prev => ({ 
                    ...prev, 
                    pagadorExterno: ibanValidation.error || 'Hay un error en el formato del IBAN' 
                }));
                hasIbanErrors = true;
            }
        }
        
        // Si hay errores de IBAN, no enviar el formulario
        if (hasIbanErrors) {
            toast.error('Por favor, corrige los errores en los campos IBAN antes de continuar.');
            return;
        }

        // Sin precio no se inscribe (falta elegir tarifa o falta un precio).
        // El back lo vuelve a comprobar; esto solo evita el viaje.
        if (!precioFijo && !esIlimitada && cotizacion.data && !cotizacion.data.ok) {
            toast.error(cotizacion.data.error?.message || 'Falta el precio de la suscripción.');
            return;
        }
        
        // Comentado: volcaba a la consola del navegador los datos personales del
        // cliente (DNI, email, teléfono, IBAN). Si hace falta depurar, mejor en local.
        /* console.log('Enviando inscripción con datos:', {
                client: {
                    nombre: data.nombre,
                    dni: data.dni,
                    email: data.email,
                    telefono: data.telefono,
                    // Cadena vacía si no se rellenó; Mongoose no puede castear '' a Date
                    fechaNacimiento: data.fechaNacimiento || null,
                    observaciones: data.observaciones,
                    createdBy: user?.fullName || 'Unknown',
                },
                subscription: {
                    nombreSuscripcion: suscripcionSeleccionada,
                    tipoPago: data.tipoPago,
                    importeSuscripcion: data.importeSuscripcion,
                    descuento: data.descuento,
                    mesesInactivos: data.mesesInactivos,
                    frecuencia: frequencyFromState(state?.frecuenciaSemanal, state?.plazasDisponibles?.length || 0),
                    pagador: isPagadorDiferente ? {
                        ...data.pagador,
                        IBAN: data.pagador?.IBAN ? cleanIban(data.pagador.IBAN) : undefined
                    } : undefined,
                    createdBy: user?.fullName || 'Unknown',
                    iban: data.iban ? cleanIban(data.iban) : undefined,
                    beneficiario: null,
                    pagador_id: selectedPagadorId,
                },
                plazas: state.plazasDisponibles.map(plaza => ({
                    ...plaza,
                    createdBy: user?.fullName || 'Unknown'
                })),
            }); */
        try {
            setIsProcessing(true);
            const response =  await crearInscripcion.mutateAsync({
                client: {
                    nombre: data.nombre,
                    dni: data.dni,
                    // Opcional: vacío no se manda (el back tampoco guardaría '').
                    email: data.email?.trim() || undefined,
                    telefono: data.telefono,
                    // Cadena vacía si no se rellenó; Mongoose no puede castear '' a Date
                    fechaNacimiento: data.fechaNacimiento || null,
                    observaciones: data.observaciones,
                    createdBy: user?.fullName || 'Unknown',
                },
                subscription: {
                    nombreSuscripcion: suscripcionSeleccionada,
                    tipoPago: data.tipoPago,
                    importeSuscripcion: data.importeSuscripcion,
                    descuento: data.descuento,
                    mesesInactivos: data.mesesInactivos,
                    frecuencia: frequencyFromState(state?.frecuenciaSemanal, state?.plazasDisponibles?.length || 0),
                    pagador: isPagadorDiferente ? {
                        ...data.pagador,
                        IBAN: data.pagador?.IBAN ? cleanIban(data.pagador.IBAN) : undefined
                    } : undefined,
                    createdBy: user?.fullName || 'Unknown',
                    iban: data.iban ? cleanIban(data.iban) : undefined,
                    beneficiario: null,
                    pagador_id: selectedPagadorId,
                    tarifasAsignadas,
                    precioFijo: puedeFijarPrecio && precioFijo,
                },
                plazas: state.plazasDisponibles.map(plaza => ({
                    ...plaza,
                    createdBy: user?.fullName || 'Unknown'
                })),
                // D2 — al venir de "Convertir en cliente", el backend enlaza la
                // ficha de prueba con este cliente y le pasa su plaza en las
                // clases futuras.
                ...(state?.prospect ? { prospectId: state.prospect.id } : {}),
                compensaciones: decisiones,
                // Solo si el aviso llegó a cargarse; si falló, no hay con qué comparar.
                ...(choques.data ? { choquesVistos: idsChoques(choques.data) } : {}),
                invitarPortal,
            });

            // Guardar el sessionId para seguimiento
            setSessionId(response.sessionId);

            // Si se creó la suscripción y hay un pagador seleccionado
            // console.log('response', response);
            // console.log('selectedPagadorId', selectedPagadorId);
            if (response.subscription._id && selectedPagadorId) {
                await updateBeneficiario.mutateAsync({
                    id: selectedPagadorId,
                    beneficiarioId: response.subscription._id
                });
            }

            // Solo navegamos cuando el proceso está completo
            if (progress?.status === 'completed') {
                toast.success('Inscripción completada con éxito');
                navigate(`/${tenantCode}/dashboard`);
            }
        } catch (error) {
            setIsProcessing(false);
            // Se llenó alguna clase desde el último refresco: no se ha guardado
            // nada. Se refresca el aviso (que avisa de lo nuevo) y se vuelve a guardar.
            if (esChoquesCambiados(error)) {
                toast.info('No se ha guardado todavía: revisa el aviso actualizado y vuelve a pulsar Guardar.', { autoClose: 8000 });
                choques.refetch();
                window.scrollTo({ top: 0, behavior: 'smooth' });
                return;
            }
            const errorPrecio = esErrorDePrecio(error);
            if (errorPrecio) {
                toast.error(errorPrecio, { autoClose: 8000 });
                cotizacion.refetch();
                return;
            }
            console.error('Error en inscripción:', error);
            toast.error('Error en la inscripción');
        }
    };

    return (
        <div className="container px-4 pb-4 pt-20 w-full mx-auto">
            <div className="pb-24">
                <h1 className="text-2xl font-bold mb-6">Inscripción de Cliente Nuevo</h1>
                
                {/* Resumen de plazas seleccionadas */}
                <PlazasResumen plazas={state.plazasDisponibles} />

                <ChoquesPlazasAviso
                    choques={choques.data}
                    isLoading={choques.isLoading}
                    isError={choques.isError}
                    decisiones={decisiones}
                    onChange={(eventoId, compensar) => setDecisiones((prev) => ({ ...prev, [eventoId]: compensar }))}
                    nombreNivel={nombreNivel}
                    nombreCliente={watch('nombre')}
                />

                <form 
                    onSubmit={handleSubmit(onSubmit)}
                    className="space-y-6"
                >
                    {/* Validación de teléfono */}
                    <TelefonoValidacion 
                        telefono={telefonoParaValidar} // ← Usar estado local en lugar de watch
                        onClienteEncontrado={setClienteEncontrado}
                        onValidationChange={setTelefonoValido}
                    />

                    {/* Datos del Cliente */}
                    <ClienteForm 
                        register={register} 
                        errors={errors} 
                        onTelefonoBlur={handleTelefonoBlur} // ← Pasar función onBlur
                        hayEmail={!!watch('email')?.trim()}
                        invitarPortal={invitarPortal}
                        onInvitarPortalChange={setInvitarPortalElegido}
                    />

                    {/* Datos de la Suscripción */}
                    <SuscripcionForm 
                        register={register}
                        errors={errors}
                        tipoPagoSeleccionado={tipoPagoSeleccionado}
                        onTipoPagoChange={handleTipoPago}
                        frecuencia={state.frecuenciaSemanal}
                        setValue={setValue}
                        onPagadorSelect={handlePagadorSelect}
                        esMenorDeEdad={esMenorDeEdad}
                        cotizacion={cotizacion.data}
                        cotizando={cotizacion.isFetching}
                        selectorTarifas={
                            <SelectorTarifas
                                cotizacion={cotizacion.data}
                                nivelesActuales={plazasBusqueda.map((p) => p.nivel_id)}
                                elegidas={tarifasElegidas}
                                onChange={(nivelId, tarifaId) => setTarifasElegidas((prev) => ({ ...prev, [nivelId]: tarifaId }))}
                                nombreNivel={nombreNivel}
                            />
                        }
                        precioFijo={precioFijo}
                        puedeFijarPrecio={puedeFijarPrecio}
                        onPrecioFijoChange={setPrecioFijo}
                        importe={watch('importeSuscripcion')}
                        descuento={watch('descuento')}
                    />

                    <FormButtons 
                        onCancel={() => navigate(-1)} 
                        isSubmitting={isSubmitting}
                        isDisabled={!telefonoValido} // ← Pasar si hay cliente duplicado
                        disabledReason="Cliente Duplicado" // ← Texto cuando hay duplicado
                    />
                </form>
            </div>
            {isProcessing && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white p-6 rounded-lg shadow-xl max-w-md w-full">
                        <div className="space-y-4">
                            <div className="flex items-center justify-center">
                                {progress?.status === 'processing' && (
                                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500" />
                                )}
                                {progress?.status === 'completed' && (
                                    <div className="text-green-500">
                                        <svg className="h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                        </svg>
                                    </div>
                                )}
                            </div>

                            <div className="text-center">
                                <h3 className="text-lg font-medium text-gray-900">
                                    {progress?.status === 'completed' 
                                        ? 'Proceso completado'
                                        : 'Procesando inscripción en eventos...'
                                    }
                                </h3>
                                <div className="mt-2">
                                    <p className="text-sm text-gray-500">
                                        {progress?.processed || 0} de {progress?.total || 0} eventos procesados
                                    </p>
                                </div>
                            </div>

                            <div className="w-full bg-gray-200 rounded-full h-2">
                                <div
                                    className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                                    style={{
                                        width: `${progress?.total ? (progress.processed / progress.total) * 100 : 0}%`
                                    }}
                                />
                            </div>

                            {progress?.status === 'completed' && (
                                <ResumenClasesCompletas clases={progress.clasesCompletas} primeraClase={progress.primeraClase} />
                            )}

                            {progress?.status === 'completed' && (
                                <div className="mt-5 flex justify-center">
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setIsProcessing(false);
                                            navigate(-1);
                                        }}
                                        className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
                                    >
                                        Continuar
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default InscripcionCliente;